import React from 'react';
import { Character } from '../types/character';
import { universeLabels, themeColors } from '../types/template';
import { X, Eye, Download, Save } from 'lucide-react';

interface Props {
  character: Character;
  onClose?: () => void;
  readOnly?: boolean;
  className?: string;
  background?: string;
}

export default function CharacterPreview({ character, onClose, readOnly = false, className = '', background = 'bg-gradient-to-br from-purple-600 to-indigo-600' }: Props) {
  const theme = themeColors[character.themeColor || 'purple'];
  const hasCharacteristics = character.sections.slice(2).some(section => section.content.trim() !== '');

  const formatText = (text: string): string => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      .replace(/\n/g, '<br>');
  };

  const handleDownload = () => {
    const exportCharacter = {
      ...character,
      readOnly: undefined,
      views: undefined,
      canDownload: undefined,
      canImport: undefined
    };

    const dataStr = JSON.stringify(exportCharacter, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const fileName = `${character.name.toLowerCase().replace(/\s+/g, '-')}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', fileName);
    linkElement.click();
  };

  const handleImport = () => {
    // Récupérer les personnages existants
    const localData = localStorage.getItem('character-creator-data');
    const characters = localData ? JSON.parse(localData) : [];

    // Créer une copie du personnage avec un nouvel ID
    const importedCharacter = {
      ...character,
      id: Date.now().toString() + Math.random().toString(36).substring(2),
      isStarred: false,
      lastModified: Date.now(),
      readOnly: undefined,
      views: undefined,
      canDownload: undefined,
      canImport: undefined
    };

    // Ajouter le personnage à la liste
    characters.push(importedCharacter);
    localStorage.setItem('character-creator-data', JSON.stringify(characters));

    alert('Personnage importé avec succès !');
    window.location.href = '/';
  };

  return (
    <div className={`min-h-screen ${background} py-8 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="character-preview w-full bg-white rounded-lg shadow-xl overflow-hidden relative">
          {/* Badge de lecture seule */}
          {readOnly && (
            <div className="absolute top-4 left-4 px-4 py-2 bg-gray-900/80 text-white rounded-full z-10 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span className="text-sm font-medium">Mode lecture seule</span>
              {character.views !== undefined && (
                <span className="text-sm text-gray-300">• {character.views} vue{character.views !== 1 ? 's' : ''}</span>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="absolute top-4 right-4 flex gap-2 z-10">
            {readOnly && (
              <>
                <button
                  onClick={handleDownload}
                  className="p-2 bg-white/90 hover:bg-white text-gray-700 rounded-full transition-colors flex items-center gap-2"
                  title="Télécharger au format JSON"
                >
                  <Download className="w-5 h-5" />
                </button>
                <button
                  onClick={handleImport}
                  className="p-2 bg-purple-600/90 hover:bg-purple-600 text-white rounded-full transition-colors flex items-center gap-2"
                  title="Importer dans ma collection"
                >
                  <Save className="w-5 h-5" />
                </button>
              </>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 bg-black/20 hover:bg-black/40 text-white rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Contenu du personnage */}
          <div className="relative">
            <div className="h-48 md:h-64 relative">
              {character.backgroundImage ? (
                <>
                  <img
                    src={character.backgroundImage}
                    alt="Background"
                    className="w-full h-full object-cover"
                  />
                  <div className={`absolute inset-0 ${theme.bg} opacity-60`} />
                </>
              ) : (
                <div className={`w-full h-full ${theme.bg}`} />
              )}
            </div>

            <div className="absolute left-8 -bottom-16">
              <div className="w-32 h-32 rounded-full border-4 border-white overflow-hidden bg-white shadow-lg">
                {character.profileImage ? (
                  <img
                    src={character.profileImage}
                    alt={character.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className={`w-full h-full ${theme.bg} flex items-center justify-center`}>
                    <span className="text-3xl font-bold text-white">
                      {character.name.charAt(0)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="pt-20 px-8 pb-8">
            {/* Titre et univers */}
            <div className="flex items-baseline gap-4 mb-8">
              <h1 className="text-4xl font-bold text-gray-900">{character.name}</h1>
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${theme.bg} text-white`}>
                {universeLabels[character.universe]}
              </span>
            </div>

            {/* Sections principales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {character.sections.slice(0, 2).map((section) => (
                <div 
                  key={section.id} 
                  className="relative overflow-hidden rounded-2xl shadow-lg group transition-all duration-300 hover:shadow-xl bg-white/80 backdrop-blur-sm p-6"
                >
                  <div className={`absolute inset-0 ${theme.bg} opacity-5 group-hover:opacity-10 transition-opacity`} />
                  <div className="relative">
                    <h2 className={`text-xl font-semibold mb-3 ${theme.text}`}>{section.title}</h2>
                    <div 
                      className="text-gray-600 whitespace-pre-wrap leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: formatText(section.content || 'Aucun contenu') }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Caractéristiques */}
            {hasCharacteristics && (
              <div className="relative overflow-hidden rounded-2xl shadow-lg mb-8 bg-white/80 backdrop-blur-sm p-6">
                <div className={`absolute inset-0 ${theme.bg} opacity-5`} />
                <div className="relative">
                  <h2 className={`text-xl font-semibold mb-6 ${theme.text}`}>Caractéristiques</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                    {character.sections.slice(2).map((section) => (
                      section.content.trim() !== '' && (
                        <div key={section.id} className="space-y-2">
                          <h3 className={`text-sm font-medium ${theme.text}`}>{section.title}</h3>
                          <div 
                            className="text-gray-700"
                            dangerouslySetInnerHTML={{ __html: formatText(section.content) }}
                          />
                        </div>
                      )
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Section Histoire */}
            {character.history && character.history.trim() !== '' && (
              <div className="relative overflow-hidden rounded-2xl shadow-lg mb-8 bg-white/80 backdrop-blur-sm p-6">
                <div className={`absolute inset-0 ${theme.bg} opacity-5`} />
                <div className="relative">
                  <h2 className={`text-xl font-semibold mb-4 ${theme.text}`}>Histoire</h2>
                  <div 
                    className="text-gray-600 whitespace-pre-wrap leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: formatText(character.history) }}
                  />
                </div>
              </div>
            )}

            {/* Section Illustrations */}
            {character.illustrations && character.illustrations.length > 0 && (
              <div className="space-y-6 mt-8">
                <h2 className={`text-xl font-semibold ${theme.text}`}>Illustrations</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {character.illustrations.map((illustration) => (
                    <div key={illustration.id} className="space-y-2">
                      <img
                        src={illustration.url}
                        alt={illustration.caption}
                        className="w-full h-48 object-cover rounded-lg shadow-md"
                      />
                      {illustration.caption && (
                        <p className="text-sm text-gray-600 text-center">{illustration.caption}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sections personnalisées */}
            {character.customSections && character.customSections.length > 0 && (
              <div className="space-y-6 mt-8">
                <h2 className={`text-xl font-semibold ${theme.text}`}>Sections personnalisées</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {character.customSections.map((section) => (
                    <div 
                      key={section.id} 
                      className="relative overflow-hidden rounded-2xl shadow-lg group transition-all duration-300 hover:shadow-xl bg-white/80 backdrop-blur-sm p-6"
                    >
                      <div className={`absolute inset-0 ${theme.bg} opacity-5 group-hover:opacity-10 transition-opacity`} />
                      <div className="relative">
                        <h3 className={`text-lg font-semibold mb-3 ${theme.text}`}>{section.title}</h3>
                        <div 
                          className="text-gray-600 whitespace-pre-wrap leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: formatText(section.content || 'Aucun contenu') }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}